import { useState, useEffect } from "react";
import {
  FileText, Upload, RefreshCw, Settings, CheckCircle2, AlertTriangle,
  CreditCard, Building2, ArrowLeftRight, Download, Trash2, X, FileCheck,
  Link2, Search, User, Calendar, Wrench, Eye, Send, Paperclip, FolderOpen,
  Sparkles, ExternalLink, FileQuestion
} from "lucide-react";
import getApiUrl from './utils/getApiUrl';

const API = getApiUrl();

const authFetch = (url, options = {}) => {
  const token = localStorage.getItem("auth_token");
  return fetch(url, {
    ...options,
    credentials: "include",
    headers: { ...(options.headers || {}), ...(token ? { Authorization: `Bearer ${token}` } : {}) },
  });
};


function RapprochementPage({ onBack, embedded = false }) {
  const [pennylaneConnected, setPennylaneConnected] = useState(false);
  const [pennylaneToken, setPennylaneToken] = useState("");
  const [showTokenInput, setShowTokenInput] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState(null);
  const [suppliers, setSuppliers] = useState([]);
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [cbTransactions, setCbTransactions] = useState([]);
  const [cbStats, setCbStats] = useState({});
  const [importedFiles, setImportedFiles] = useState([]);
  const [rapprochementResult, setRapprochementResult] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  
  // État pour l'onglet Factures Chantiers
  const [unlinkedTransactions, setUnlinkedTransactions] = useState([]);
  const [clients, setClients] = useState([]);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [searchClient, setSearchClient] = useState("");
  const [linkComment, setLinkComment] = useState("");
  const [showLinkModal, setShowLinkModal] = useState(false);
  
  // Nouvel état pour CB vs Pennylane
  const [missingInPennylane, setMissingInPennylane] = useState([]);
  const [filterSupplier, setFilterSupplier] = useState('');
  
  // État pour l'upload de facture
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadTransaction, setUploadTransaction] = useState(null);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadComment, setUploadComment] = useState('');
  
  // État pour les factures Pennylane sans client
  const [pennylaneInvoices, setPennylaneInvoices] = useState([]);
  const [pennylaneCategories, setPennylaneCategories] = useState({ client_categories: [], comptable_categories: [] });
  const [loadingPennylane, setLoadingPennylane] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [showLinkInvoiceModal, setShowLinkInvoiceModal] = useState(false);
  const [searchClientInvoice, setSearchClientInvoice] = useState("");
  const [invoiceLinkComment, setInvoiceLinkComment] = useState("");
  const [aiSuggestion, setAiSuggestion] = useState(null);
  const [loadingAi, setLoadingAi] = useState(false);

  useEffect(() => {
    checkPennylaneStatus();
    loadCBTransactions();
    loadImportedFiles();
  }, []);

  useEffect(() => {
    if (activeTab === 'factures') {
      loadUnlinkedTransactions();
      loadClients();
    }
    if (activeTab === 'missing') {
      loadMissingTransactions();
    }
    if (activeTab === 'pennylane-invoices') {
      loadPennylaneInvoices();
      loadClients();
    }
  }, [activeTab]);

  const checkPennylaneStatus = async () => {
    try {
      const resp = await authFetch(`${API}/api/rapprochement/pennylane/status`);
      if (!resp.ok) return;
      const data = await resp.json();
      setPennylaneConnected(data.connected);
      if (data.connected) loadSuppliers();
    } catch (e) {
      console.error("checkPennylaneStatus error:", e);
    }
  };

  const loadImportedFiles = async () => {
    try {
      const resp = await authFetch(`${API}/api/rapprochement/releves/files`);
      if (!resp.ok) return;
      const data = await resp.json();
      setImportedFiles(data.files || []);
    } catch (e) {
      console.error("loadImportedFiles error:", e);
    }
  };

  const loadUnlinkedTransactions = async () => {
    try {
      const resp = await authFetch(`${API}/api/rapprochement/unlinked`);
      if (!resp.ok) return;
      const data = await resp.json();
      setUnlinkedTransactions(data.transactions || []);
    } catch (e) {
      console.error("loadUnlinkedTransactions error:", e);
    }
  };

  const loadClients = async () => {
    try {
      const resp = await authFetch(`${API}/api/poles/Super%20Admin/clients?limit=500`);
      if (!resp.ok) return;
      const data = await resp.json();
      setClients(data.clients || []);
    } catch (e) {
      console.error("loadClients error:", e);
    }
  };
  
  const loadMissingTransactions = async () => {
    if (!pennylaneConnected) return;
    setLoading(true);
    try {
      const resp = await authFetch(`${API}/api/rapprochement/match`);
      if (resp.ok) {
        const data = await resp.json();
        if (data.unmatched_cb) {
          setMissingInPennylane(data.unmatched_cb);
          setRapprochementResult(data);
        }
      } else {
        console.error("Erreur rapprochement:", resp.status);
      }
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
  };
  
  const loadPennylaneInvoices = async () => {
    if (!pennylaneConnected) return;
    setLoadingPennylane(true);
    try {
      const resp = await authFetch(`${API}/api/rapprochement/pennylane/invoices-without-client`);
      if (resp.ok) {
        const data = await resp.json();
        setPennylaneInvoices(data.invoices || []);
      }
    } catch (e) {
      console.error("loadPennylaneInvoices error:", e);
    }
    setLoadingPennylane(false);
  };
  
  const suggestClientForInvoice = async (invoice) => {
    setLoadingAi(true);
    setAiSuggestion(null);
    try {
      const resp = await authFetch(`${API}/api/rapprochement/suggest-client`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          supplier_name: invoice.supplier_name,
          amount: invoice.amount,
          date: invoice.date,
          label: invoice.label
        })
      });
      if (resp.ok) {
        const data = await resp.json();
        setAiSuggestion(data);
      }
    } catch (e) {
      console.error("suggestClientForInvoice error:", e);
    }
    setLoadingAi(false);
  };
  
  const openLinkInvoiceModal = (invoice) => {
    setSelectedInvoice(invoice);
    setSearchClientInvoice("");
    setInvoiceLinkComment("");
    setAiSuggestion(null);
    setShowLinkInvoiceModal(true);
    suggestClientForInvoice(invoice);
  };
  
  const linkInvoiceToClient = async (client) => {
    if (!selectedInvoice) return;
    setLoading(true);
    try {
      const resp = await authFetch(`${API}/api/rapprochement/link-invoice-client`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          invoice_id: selectedInvoice.id,
          client_row: client.row_number,
          client_name: `${client.data.NOM || ''} ${client.data.PRÉNOM || ''}`.trim(),
          comment: invoiceLinkComment
        })
      });
      if (resp.ok) {
        setSuccessMsg(`Facture liée à ${client.data.NOM || ''} ${client.data.PRÉNOM || ''}`);
        setShowLinkInvoiceModal(false);
        setSelectedInvoice(null);
        // Retirer la facture de la liste
        setPennylaneInvoices(prev => prev.filter(inv => inv.id !== selectedInvoice.id));
        setTimeout(() => setSuccessMsg(null), 5000);
      } else {
        const data = await resp.json().catch(() => ({}));
        setError(data.detail || "Erreur lors de la liaison");
      }
    } catch (e) {
      setError(e.message);
    }
    setLoading(false);
  };
  
  const fixAberrantAmounts = async () => {
    setLoading(true);
    setError(null);
    try {
      const resp = await authFetch(`${API}/api/rapprochement/releves/fix-amounts`, { method: 'POST' });
      const data = await resp.json();
      if (resp.ok && data.success) {
        setSuccessMsg(`${data.fixed} transactions corrigées, ${data.deleted} supprimées`);
        await loadCBTransactions();
        setTimeout(() => setSuccessMsg(null), 5000);
      } else {
        setError(data.detail || data.message || "Erreur lors de la correction");
      }
    } catch (e) {
      setError(e.message);
    }
    setLoading(false);
  };

  const configurePennylane = async () => {
    if (!pennylaneToken) return;
    setLoading(true);
    setError(null);
    try {
      const resp = await authFetch(`${API}/api/rapprochement/pennylane/config`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: pennylaneToken })
      });
      if (resp.ok) {
        setPennylaneConnected(true);
        setShowTokenInput(false);
        loadSuppliers();
      } else {
        const data = await resp.json();
        setError(data.detail || "Erreur de connexion");
      }
    } catch (e) {
      setError(e.message);
    }
    setLoading(false);
  };

  const loadSuppliers = async () => {
    try {
      const resp = await authFetch(`${API}/api/rapprochement/pennylane/suppliers`);
      if (!resp.ok) return;
      const data = await resp.json();
      setSuppliers(data.suppliers || []);
    } catch (e) {
      console.error("loadSuppliers error:", e);
    }
  };

  const loadCBTransactions = async () => {
    try {
      const resp = await authFetch(`${API}/api/rapprochement/releves/transactions`);
      if (!resp.ok) return;
      const data = await resp.json();
      setCbTransactions(data.transactions || []);
      setCbStats(data.suppliers_stats || {});
    } catch (e) {
      console.error("loadCBTransactions error:", e);
    }
  };

  const handleFileUpload = async (e) => {
    const files = e.target.files;
    if (!files.length) return;
    setLoading(true);
    setError(null);
    for (const file of files) {
      try {
        const formData = new FormData();
        formData.append('file', file);
        const resp = await authFetch(`${API}/api/rapprochement/releves/upload`, {
          method: 'POST',
          body: formData
        });
        if (!resp.ok) {
          const data = await resp.json();
          throw new Error(data.detail || "Erreur upload");
        }
      } catch (e) {
        setError(e.message);
      }
    }
    await loadCBTransactions();
    await loadImportedFiles();
    setLoading(false);
    e.target.value = '';
  };

  const clearTransactions = async () => {
    if (!window.confirm("Vider toutes les transactions CB importées ? Cette action est irréversible.")) return;
    try {
      await authFetch(`${API}/api/rapprochement/releves/clear`, { method: 'DELETE' });
      await loadCBTransactions();
      await loadImportedFiles();
      setRapprochementResult(null);
    } catch (e) {
      console.error(e);
    }
  };

  const performRapprochement = async () => {
    if (!pennylaneConnected) {
      setError("Connectez d'abord Pennylane");
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const url = selectedSupplier 
        ? `${API}/api/rapprochement/match?supplier=${encodeURIComponent(selectedSupplier)}`
        : `${API}/api/rapprochement/match`;
      
      console.log("Fetching:", url);
      const resp = await authFetch(url);
      console.log("Response status:", resp.status);
      
      if (!resp.ok) {
        setError(`Erreur serveur: ${resp.status}`);
        setLoading(false);
        return;
      }
      
      const text = await resp.text();
      console.log("Response length:", text.length);
      
      let data;
      try {
        data = JSON.parse(text);
      } catch (parseErr) {
        console.error("JSON parse error:", parseErr);
        setError("Erreur de parsing des données");
        setLoading(false);
        return;
      }
      
      console.log("Parsed data, unmatched_cb count:", data.unmatched_cb?.length);
      setRapprochementResult(data);
      setMissingInPennylane(data.unmatched_cb || []);
      setActiveTab('missing');
    } catch (e) {
      console.error("performRapprochement error:", e);
      setError(`Erreur: ${e.message}`);
    }
    setLoading(false);
  };

  const exportMissingCSV = () => {
    if (!missingInPennylane.length) return;
    
    const headers = ['Date', 'Montant', 'Description', 'Fournisseur'];
    const rows = missingInPennylane.map(t => [
      t.date,
      t.amount.toFixed(2).replace('.', ','),
      (t.description || t.full_line || '').replace(/;/g, ' ').slice(0, 60),
      t.supplier_detected || 'Autre'
    ]);
    
    const csv = [headers.join(';'), ...rows.map(r => r.join(';'))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `transactions_manquantes_pennylane_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };
  
  const openUploadModal = (transaction) => {
    setUploadTransaction(transaction);
    setUploadFile(null);
    setUploadComment('');
    setShowUploadModal(true);
  };
  
  const handleUploadInvoice = async () => {
    if (!uploadFile || !uploadTransaction) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const formData = new FormData();
      formData.append('file', uploadFile);
      formData.append('transaction_date', uploadTransaction.date);
      formData.append('transaction_amount', uploadTransaction.amount.toString());
      formData.append('supplier', uploadTransaction.supplier_detected || 'Autre');
      formData.append('comment', uploadComment);
      
      const resp = await authFetch(`${API}/api/rapprochement/invoice/upload`, {
        method: 'POST',
        body: formData
      });
      
      if (resp.ok) {
        setSuccessMsg(`Facture uploadée pour ${uploadTransaction.supplier_detected || 'transaction'} - ${uploadTransaction.amount.toFixed(2)}€`);
        setShowUploadModal(false);
        setUploadTransaction(null);
        setUploadFile(null);
        
        // Retirer la transaction de la liste des manquantes
        setMissingInPennylane(prev => prev.filter(t => 
          !(t.date === uploadTransaction.date && t.amount === uploadTransaction.amount)
        ));
        
        setTimeout(() => setSuccessMsg(null), 5000);
      } else {
        const data = await resp.json().catch(() => ({}));
        setError(data.detail || "Erreur lors de l'upload");
      }
    } catch (e) {
      setError(e.message);
    }
    setLoading(false);
  };

  const linkTransactionToClient = async (client) => {
    if (!selectedTransaction) return;
    setLoading(true);
    try {
      const resp = await authFetch(`${API}/api/rapprochement/link`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          transaction_date: selectedTransaction.date,
          transaction_amount: selectedTransaction.amount,
          client_row: client.row_number,
          client_name: `${client.data.NOM || ''} ${client.data.PRÉNOM || ''}`.trim(),
          comment: linkComment
        })
      });
      if (resp.ok) {
        setShowLinkModal(false);
        setSelectedTransaction(null);
        setLinkComment("");
        await loadUnlinkedTransactions();
      } else {
        const data = await resp.json();
        setError(data.detail || "Erreur liaison");
      }
    } catch (e) {
      setError(e.message);
    }
    setLoading(false);
  };

  const filteredClients = clients.filter(c => {
    if (!searchClient) return true;
    const search = searchClient.toLowerCase();
    const nom = (c.data.NOM || '').toLowerCase();
    const prenom = (c.data.PRÉNOM || '').toLowerCase();
    const dpt = (c.data.DPT || '').toLowerCase();
    const ville = (c.data.VILLE || '').toLowerCase();
    const adresse = (c.data['ADRESSE DU CHANTIER'] || '').toLowerCase();
    const tel = (c.data['TEL MOBILE'] || c.data['TELEPHONE'] || '').toLowerCase();
    return nom.includes(search) || prenom.includes(search) || dpt.includes(search) || ville.includes(search) || adresse.includes(search) || tel.includes(search);
  });
  
  // Filtrer les transactions manquantes par fournisseur
  const filteredMissing = filterSupplier 
    ? missingInPennylane.filter(t => t.supplier_detected === filterSupplier)
    : missingInPennylane;
  
  // Vérifier s'il y a des montants aberrants
  const hasAberrantAmounts = cbTransactions.some(t => t.amount > 50000);

  return (
    <div className={embedded ? "h-full bg-slate-900 text-white" : "min-h-screen bg-slate-900 text-white"}>
      <header className="bg-slate-800/50 border-b border-slate-700/50 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            {onBack && !embedded && (
              <button onClick={onBack} className="p-2 hover:bg-slate-700/50 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            )}
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-xl flex items-center justify-center">
                <ArrowLeftRight className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold">Rapprochement Bancaire</h1>
                <p className="text-slate-400 text-sm">Pennylane ↔ Relevés CB</p>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-4">
            {pennylaneConnected ? (
              <div className="flex items-center gap-2 bg-green-500/20 text-green-400 px-4 py-2 rounded-lg">
                <CheckCircle2 className="w-4 h-4" />
                <span className="text-sm">Pennylane connecté</span>
              </div>
            ) : (
              <button onClick={() => setShowTokenInput(true)} className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 px-4 py-2 rounded-lg">
                <Settings className="w-4 h-4" />
                <span>Connecter Pennylane</span>
              </button>
            )}
          </div>
        </div>
      </header>

      {/* Modal Token Pennylane */}
      {showTokenInput && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-slate-800 rounded-xl p-6 w-full max-w-md border border-slate-700">
            <h3 className="text-lg font-semibold mb-4">Configuration Pennylane</h3>
            <input type="text" value={pennylaneToken} onChange={(e) => setPennylaneToken(e.target.value)} placeholder="Token Pennylane..." className="w-full bg-slate-700 border border-slate-600 rounded-lg px-4 py-2 mb-4" />
            {error && <p className="text-red-400 text-sm mb-4">{error}</p>}
            <div className="flex gap-2">
              <button onClick={() => { setShowTokenInput(false); setError(null); }} className="flex-1 px-4 py-2 bg-slate-700 hover:bg-slate-600 rounded-lg">Annuler</button>
              <button onClick={configurePennylane} disabled={loading || !pennylaneToken} className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg disabled:opacity-50">{loading ? "..." : "Connecter"}</button>
            </div>
          </div>
        </div>
      )}

      {/* Modal liaison transaction → client */}
      {showLinkModal && selectedTransaction && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-slate-800 rounded-xl p-6 w-full max-w-2xl border border-slate-700 max-h-[80vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Lier à un chantier</h3>
              <button onClick={() => setShowLinkModal(false)} className="p-2 hover:bg-slate-700 rounded-lg"><X className="w-5 h-5" /></button>
            </div>
            <div className="bg-slate-700/50 rounded-lg p-4 mb-4">
              <p className="text-sm text-slate-400">Transaction:</p>
              <p className="font-semibold">{selectedTransaction.date} - {selectedTransaction.amount.toFixed(2)} €</p>
            </div>
            <input type="text" value={linkComment} onChange={(e) => setLinkComment(e.target.value)} placeholder="Commentaire (optionnel)" className="w-full bg-slate-700 border border-slate-600 rounded-lg px-4 py-2 mb-4" />
            <div className="relative mb-4">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input type="text" value={searchClient} onChange={(e) => setSearchClient(e.target.value)} placeholder="Rechercher par nom, prénom ou département..." className="w-full bg-slate-700 border border-slate-600 rounded-lg pl-10 pr-4 py-2" />
            </div>
            <p className="text-sm text-slate-400 mb-2">{filteredClients.length} clients trouvés</p>
            <div className="flex-1 overflow-y-auto space-y-2">
              {filteredClients.slice(0, 50).map(client => (
                <button key={client.row_number} onClick={() => linkTransactionToClient(client)} className="w-full flex items-center gap-3 p-3 bg-slate-700/50 hover:bg-slate-600/50 rounded-lg text-left">
                  <User className="w-5 h-5 text-blue-400" />
                  <div className="flex-1">
                    <p className="font-medium">
                      {client.data.NOM || ''} {client.data.PRÉNOM || ''} 
                      {!client.data.NOM && !client.data.PRÉNOM && `Client #${client.row_number}`}
                    </p>
                    <p className="text-sm text-slate-400">
                      Dpt {client.data.DPT || '?'} 
                      {client.data.VILLE && ` - ${client.data.VILLE}`}
                      {client.data['ADRESSE DU CHANTIER'] && ` - ${client.data['ADRESSE DU CHANTIER'].slice(0, 30)}`}
                    </p>
                  </div>
                  <span className="text-xs text-slate-500">#{client.row_number}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Modal upload facture */}
      {showUploadModal && uploadTransaction && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-slate-800 rounded-xl p-6 w-full max-w-lg border border-slate-700">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold flex items-center gap-2">
                <Paperclip className="w-5 h-5 text-blue-400" />
                Ajouter la facture
              </h3>
              <button onClick={() => setShowUploadModal(false)} className="p-2 hover:bg-slate-700 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="bg-slate-700/50 rounded-lg p-4 mb-4">
              <p className="text-sm text-slate-400">Transaction:</p>
              <p className="font-semibold">{uploadTransaction.date} - {uploadTransaction.amount.toFixed(2)} €</p>
              <p className="text-sm text-slate-400">{uploadTransaction.supplier_detected || 'Autre'}</p>
              <p className="text-xs text-slate-500 mt-1">{uploadTransaction.description || uploadTransaction.full_line?.slice(0, 50)}</p>
            </div>

            <div className="mb-4">
              <label className="block text-sm text-slate-400 mb-2">Fichier facture (PDF, JPG, PNG)</label>
              <input 
                type="file" 
                accept=".pdf,.jpg,.jpeg,.png"
                onChange={(e) => setUploadFile(e.target.files[0])}
                className="w-full bg-slate-700 border border-slate-600 rounded-lg px-4 py-2 file:mr-4 file:py-1 file:px-3 file:rounded file:border-0 file:bg-blue-600 file:text-white file:cursor-pointer"
              />
              {uploadFile && (
                <p className="text-sm text-green-400 mt-2">✓ {uploadFile.name}</p>
              )}
            </div>

            <div className="mb-4">
              <label className="block text-sm text-slate-400 mb-2">Commentaire (optionnel)</label>
              <input 
                type="text"
                value={uploadComment}
                onChange={(e) => setUploadComment(e.target.value)}
                placeholder="Ex: Facture récupérée sur espace pro..."
                className="w-full bg-slate-700 border border-slate-600 rounded-lg px-4 py-2"
              />
            </div>

            <div className="flex gap-2">
              <button 
                onClick={() => setShowUploadModal(false)} 
                className="flex-1 px-4 py-2 bg-slate-700 hover:bg-slate-600 rounded-lg"
              >
                Annuler
              </button>
              <button 
                onClick={handleUploadInvoice}
                disabled={!uploadFile || loading}
                className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {loading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                {loading ? "Upload..." : "Enregistrer"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal liaison facture Pennylane → client */}
      {showLinkInvoiceModal && selectedInvoice && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-slate-800 rounded-xl p-6 w-full max-w-2xl border border-slate-700 max-h-[85vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold flex items-center gap-2">
                <FolderOpen className="w-5 h-5 text-orange-400" />
                Associer à un client CRM
              </h3>
              <button onClick={() => setShowLinkInvoiceModal(false)} className="p-2 hover:bg-slate-700 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            {/* Info facture */}
            <div className="bg-slate-700/50 rounded-lg p-4 mb-4">
              <p className="text-sm text-slate-400">Facture Pennylane:</p>
              <p className="font-semibold">{selectedInvoice.supplier_name}</p>
              <p className="text-lg text-emerald-400">{selectedInvoice.amount.toFixed(2)} €</p>
              <p className="text-sm text-slate-400 mt-1">{selectedInvoice.date} - {selectedInvoice.label?.slice(0, 60)}</p>
              {selectedInvoice.public_file_url && (
                <a href={selectedInvoice.public_file_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-blue-400 text-sm mt-2 hover:underline">
                  <ExternalLink className="w-3 h-3" /> Voir la facture
                </a>
              )}
            </div>

            {/* Suggestion IA */}
            {loadingAi && (
              <div className="bg-purple-500/20 rounded-lg p-4 mb-4 flex items-center gap-3">
                <RefreshCw className="w-5 h-5 text-purple-400 animate-spin" />
                <span className="text-purple-300">Analyse IA en cours...</span>
              </div>
            )}
            {aiSuggestion && !loadingAi && (
              <div className="bg-purple-500/20 rounded-lg p-4 mb-4 border border-purple-500/30">
                <div className="flex items-center gap-2 mb-2">
                  <Sparkles className="w-4 h-4 text-purple-400" />
                  <span className="text-purple-300 font-medium">Suggestion IA</span>
                </div>
                {aiSuggestion.suggestion ? (
                  <div className="space-y-2">
                    <p className="text-white">{aiSuggestion.suggestion.name}</p>
                    <p className="text-sm text-slate-400">Dpt {aiSuggestion.suggestion.dpt}</p>
                    <p className="text-xs text-purple-300">{aiSuggestion.explanation}</p>
                    <button 
                      onClick={() => {
                        const suggested = clients.find(c => c.row_number === aiSuggestion.suggestion.row_number);
                        if (suggested) linkInvoiceToClient(suggested);
                      }}
                      className="mt-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 rounded-lg text-sm flex items-center gap-2"
                    >
                      <CheckCircle2 className="w-4 h-4" /> Valider cette suggestion
                    </button>
                  </div>
                ) : (
                  <p className="text-slate-400 text-sm">Aucune suggestion automatique disponible</p>
                )}
              </div>
            )}

            {/* Recherche manuelle */}
            <div className="relative mb-3">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input 
                type="text" 
                value={searchClientInvoice} 
                onChange={(e) => setSearchClientInvoice(e.target.value)} 
                placeholder="Rechercher par nom, département..." 
                className="w-full bg-slate-700 border border-slate-600 rounded-lg pl-10 pr-4 py-2" 
              />
            </div>

            <input 
              type="text" 
              value={invoiceLinkComment} 
              onChange={(e) => setInvoiceLinkComment(e.target.value)} 
              placeholder="Commentaire (optionnel)" 
              className="w-full bg-slate-700 border border-slate-600 rounded-lg px-4 py-2 mb-3" 
            />

            <p className="text-sm text-slate-400 mb-2">
              {clients.filter(c => {
                if (!searchClientInvoice) return true;
                const search = searchClientInvoice.toLowerCase();
                const nom = (c.data.NOM || '').toLowerCase();
                const prenom = (c.data.PRÉNOM || '').toLowerCase();
                const dpt = (c.data.DPT || '').toLowerCase();
                return nom.includes(search) || prenom.includes(search) || dpt.includes(search);
              }).length} clients
            </p>
            
            <div className="flex-1 overflow-y-auto space-y-2">
              {clients.filter(c => {
                if (!searchClientInvoice) return true;
                const search = searchClientInvoice.toLowerCase();
                const nom = (c.data.NOM || '').toLowerCase();
                const prenom = (c.data.PRÉNOM || '').toLowerCase();
                const dpt = (c.data.DPT || '').toLowerCase();
                return nom.includes(search) || prenom.includes(search) || dpt.includes(search);
              }).slice(0, 50).map(client => (
                <button 
                  key={client.row_number} 
                  onClick={() => linkInvoiceToClient(client)} 
                  className="w-full flex items-center gap-3 p-3 bg-slate-700/50 hover:bg-slate-600/50 rounded-lg text-left"
                >
                  <User className="w-5 h-5 text-blue-400" />
                  <div className="flex-1">
                    <p className="font-medium">{client.data.NOM || ''} {client.data.PRÉNOM || ''} - Dpt {client.data.DPT || '?'}</p>
                    <p className="text-sm text-slate-400">{client.data.VILLE || ''} {client.data['ADRESSE DU CHANTIER'] ? `- ${client.data['ADRESSE DU CHANTIER'].slice(0, 30)}` : ''}</p>
                  </div>
                  <span className="text-xs text-slate-500">#{client.row_number}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Onglets */}
      <div className="border-b border-slate-700/50 px-6">
        <div className="flex gap-1 overflow-x-auto">
          <TabButton active={activeTab === 'overview'} onClick={() => setActiveTab('overview')} icon={FileText} label="Vue d'ensemble" />
          <TabButton active={activeTab === 'import'} onClick={() => setActiveTab('import')} icon={Upload} label="Import CB" />
          <TabButton active={activeTab === 'transactions'} onClick={() => setActiveTab('transactions')} icon={Eye} label="Transactions CB" badge={cbTransactions.length} />
          <TabButton active={activeTab === 'missing'} onClick={() => setActiveTab('missing')} icon={AlertTriangle} label="Manquantes Pennylane" badge={missingInPennylane.length} color="red" />
          <TabButton active={activeTab === 'pennylane-invoices'} onClick={() => setActiveTab('pennylane-invoices')} icon={FileQuestion} label="Factures Pennylane" badge={pennylaneInvoices.length} color="orange" />
          <TabButton active={activeTab === 'factures'} onClick={() => setActiveTab('factures')} icon={Link2} label="Lier aux Chantiers" />
        </div>
      </div>

      <div className="p-6">
        {error && <div className="mb-4 p-4 bg-red-500/20 border border-red-500/50 rounded-lg text-red-400 flex items-center justify-between">{error}<button onClick={() => setError(null)}><X className="w-4 h-4" /></button></div>}
        {successMsg && <div className="mb-4 p-4 bg-green-500/20 border border-green-500/50 rounded-lg text-green-400">{successMsg}</div>}
        
        {/* Alerte montants aberrants */}
        {hasAberrantAmounts && (
          <div className="mb-4 p-4 bg-orange-500/20 border border-orange-500/50 rounded-lg flex items-center justify-between">
            <div className="flex items-center gap-3">
              <AlertTriangle className="w-5 h-5 text-orange-400" />
              <div>
                <p className="font-medium text-orange-400">Montants aberrants détectés</p>
                <p className="text-sm text-slate-400">Certaines transactions ont des montants incorrects (&gt; 50 000€)</p>
              </div>
            </div>
            <button onClick={fixAberrantAmounts} disabled={loading} className="flex items-center gap-2 px-4 py-2 bg-orange-600 hover:bg-orange-700 rounded-lg">
              <Wrench className="w-4 h-4" />{loading ? "Correction..." : "Corriger"}
            </button>
          </div>
        )}

        {/* ONGLET: Vue d'ensemble */}
        {activeTab === 'overview' && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <StatCard icon={FileCheck} color="blue" title="Fichiers importés" value={importedFiles.length} />
              <StatCard icon={CreditCard} color="purple" title="Transactions CB" value={cbTransactions.length} />
              <StatCard icon={Building2} color="green" title="Fournisseurs Pennylane" value={suppliers.length} />
              <StatCard icon={AlertTriangle} color="red" title="Manquantes" value={missingInPennylane.length} />
            </div>

            <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700/50">
              <h3 className="font-semibold mb-4">Lancer le rapprochement</h3>
              <div className="flex gap-4 items-end">
                <div className="flex-1">
                  <label className="block text-sm text-slate-400 mb-2">Fournisseur (optionnel)</label>
                  <select value={selectedSupplier || ''} onChange={(e) => setSelectedSupplier(e.target.value || null)} className="w-full bg-slate-700 border border-slate-600 rounded-lg px-4 py-2">
                    <option value="">Tous les fournisseurs</option>
                    {suppliers.map(s => <option key={s.name} value={s.name}>{s.name} ({s.invoice_count} factures)</option>)}
                  </select>
                </div>
                <button onClick={performRapprochement} disabled={loading || !pennylaneConnected || cbTransactions.length === 0} className="px-6 py-2 bg-green-600 hover:bg-green-700 rounded-lg disabled:opacity-50 flex items-center gap-2">
                  {loading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <ArrowLeftRight className="w-4 h-4" />}
                  Analyser
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ONGLET: Import CB */}
        {activeTab === 'import' && (
          <div className="space-y-6">
            <div className="bg-slate-800/50 rounded-xl p-8 border-2 border-dashed border-slate-600 text-center">
              <Upload className="w-12 h-12 text-slate-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">Importer des relevés CB</h3>
              <p className="text-emerald-400 text-sm mb-4">✨ Les données sont persistées en base</p>
              <input type="file" accept=".pdf" multiple onChange={handleFileUpload} className="hidden" id="file-upload" />
              <label htmlFor="file-upload" className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 rounded-lg cursor-pointer">
                <Upload className="w-4 h-4" />Sélectionner des fichiers PDF
              </label>
            </div>

            {importedFiles.length > 0 && (
              <div className="bg-slate-800/50 rounded-xl p-4 border border-slate-700/50">
                <h4 className="font-medium mb-3 flex items-center gap-2">
                  <FileCheck className="w-4 h-4 text-emerald-400" />
                  {importedFiles.length} fichiers importés
                </h4>
                <div className="flex flex-wrap gap-2">
                  {importedFiles.map((file, i) => (
                    <span key={i} className="px-3 py-1 bg-slate-700/50 rounded-full text-sm">{file}</span>
                  ))}
                </div>
              </div>
            )}

            {cbTransactions.length > 0 && (
              <div className="flex items-center justify-between">
                <span>{cbTransactions.length} transactions extraites</span>
                <button onClick={clearTransactions} className="flex items-center gap-2 px-4 py-2 bg-red-600/20 text-red-400 hover:bg-red-600/30 rounded-lg">
                  <Trash2 className="w-4 h-4" />Vider tout
                </button>
              </div>
            )}
          </div>
        )}

        {/* ONGLET: Transactions CB */}
        {activeTab === 'transactions' && (
          <div className="space-y-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {Object.entries(cbStats).map(([name, stats]) => (
                <div key={name} className="bg-slate-800/50 rounded-xl p-4 border border-slate-700/50">
                  <h4 className="font-medium text-sm text-slate-400">{name || 'Autre'}</h4>
                  <p className="text-2xl font-bold mt-1">{stats.count}</p>
                  <p className="text-sm text-slate-400">
                    {stats.total.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €
                  </p>
                </div>
              ))}
            </div>

            <div className="bg-slate-800/50 rounded-xl border border-slate-700/50 overflow-hidden">
              <div className="p-4 border-b border-slate-700/50 flex items-center justify-between">
                <h3 className="font-semibold">{cbTransactions.length} transactions CB</h3>
                <select value={filterSupplier} onChange={(e) => setFilterSupplier(e.target.value)} className="bg-slate-700 border border-slate-600 rounded-lg px-3 py-1 text-sm">
                  <option value="">Tous les fournisseurs</option>
                  {Object.keys(cbStats).map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div className="overflow-x-auto max-h-[500px]">
                <table className="w-full text-sm">
                  <thead className="bg-slate-900/50 sticky top-0">
                    <tr>
                      <th className="text-left px-4 py-3 text-slate-400">Date</th>
                      <th className="text-left px-4 py-3 text-slate-400">Fournisseur</th>
                      <th className="text-left px-4 py-3 text-slate-400">Description</th>
                      <th className="text-right px-4 py-3 text-slate-400">Montant</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-700/50">
                    {cbTransactions
                      .filter(t => !filterSupplier || t.supplier_detected === filterSupplier)
                      .slice(0, 100)
                      .map((t, i) => (
                      <tr key={i} className={`hover:bg-slate-700/30 ${t.amount > 50000 ? 'bg-red-500/10' : ''}`}>
                        <td className="px-4 py-2 whitespace-nowrap">{t.date}</td>
                        <td className="px-4 py-2">
                          <span className={`px-2 py-1 rounded text-xs ${
                            t.supplier_detected === 'LEROY MERLIN' ? 'bg-green-500/20 text-green-400' :
                            t.supplier_detected === 'BRICO DEPOT' ? 'bg-orange-500/20 text-orange-400' :
                            t.supplier_detected === 'AMAZON' ? 'bg-yellow-500/20 text-yellow-400' :
                            'bg-slate-700 text-slate-300'
                          }`}>{t.supplier_detected || 'Autre'}</span>
                        </td>
                        <td className="px-4 py-2 text-slate-400 max-w-xs truncate">{t.description || t.full_line?.slice(0, 50)}</td>
                        <td className={`px-4 py-2 text-right font-medium ${t.amount > 50000 ? 'text-red-400' : ''}`}>
                          {t.amount > 50000 ? '⚠️ ' : ''}{t.amount.toFixed(2)} €
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* ONGLET: Manquantes dans Pennylane */}
        {activeTab === 'missing' && (
          <div className="space-y-6">
            {!pennylaneConnected ? (
              <div className="text-center py-12 bg-slate-800/50 rounded-xl">
                <Settings className="w-16 h-16 text-slate-600 mx-auto mb-4" />
                <h3 className="text-xl font-semibold mb-2">Connectez Pennylane</h3>
                <p className="text-slate-400 mb-6">Pour voir les transactions manquantes</p>
                <button onClick={() => setShowTokenInput(true)} className="px-6 py-3 bg-blue-600 hover:bg-blue-700 rounded-lg">Connecter</button>
              </div>
            ) : missingInPennylane.length === 0 && !loading ? (
              <div className="text-center py-12 bg-slate-800/50 rounded-xl">
                <RefreshCw className="w-16 h-16 text-slate-600 mx-auto mb-4" />
                <h3 className="text-xl font-semibold mb-2">Lancez l'analyse</h3>
                <button onClick={performRapprochement} disabled={loading} className="px-6 py-3 bg-green-600 hover:bg-green-700 rounded-lg">
                  {loading ? "Analyse..." : "Comparer CB ↔ Pennylane"}
                </button>
              </div>
            ) : (
              <>
                <div className="bg-gradient-to-r from-red-500/20 to-orange-500/20 rounded-xl p-6 border border-red-500/30">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-lg font-semibold text-red-400 flex items-center gap-2">
                        <AlertTriangle className="w-5 h-5" />
                        {filteredMissing.length} transactions CB sans facture Pennylane
                      </h3>
                      <p className="text-slate-400 mt-1">
                        Total: {filteredMissing.reduce((s, t) => s + (t.amount <= 50000 ? t.amount : 0), 0).toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €
                      </p>
                    </div>
                    <button onClick={exportMissingCSV} className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg">
                      <Download className="w-4 h-4" />Exporter CSV
                    </button>
                  </div>
                </div>

                <div className="flex flex-wrap gap-4 items-center">
                  <select value={filterSupplier} onChange={(e) => setFilterSupplier(e.target.value)} className="bg-slate-700 border border-slate-600 rounded-lg px-4 py-2">
                    <option value="">Tous les fournisseurs ({missingInPennylane.length})</option>
                    {[...new Set(missingInPennylane.map(t => t.supplier_detected).filter(Boolean))]
                      .map(s => ({
                        name: s,
                        count: missingInPennylane.filter(t => t.supplier_detected === s).length,
                        total: missingInPennylane.filter(t => t.supplier_detected === s).reduce((sum, t) => sum + t.amount, 0)
                      }))
                      .sort((a, b) => b.count - a.count)
                      .map(s => (
                        <option key={s.name} value={s.name}>{s.name} ({s.count}) - {s.total.toFixed(0)}€</option>
                      ))
                    }
                  </select>
                  <span className="text-slate-400">{filteredMissing.length} transactions affichées</span>
                </div>

                <div className="bg-slate-800/50 rounded-xl border border-slate-700/50 overflow-hidden">
                  <div className="overflow-x-auto max-h-[500px]">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-900/50 sticky top-0">
                        <tr>
                          <th className="text-left px-4 py-3 text-slate-400">Date</th>
                          <th className="text-left px-4 py-3 text-slate-400">Fournisseur</th>
                          <th className="text-left px-4 py-3 text-slate-400">Description</th>
                          <th className="text-right px-4 py-3 text-slate-400">Montant</th>
                          <th className="text-center px-4 py-3 text-slate-400">Action</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-700/50">
                        {filteredMissing.filter(t => t.amount <= 50000).map((t, i) => (
                          <tr key={i} className="hover:bg-slate-700/30">
                            <td className="px-4 py-2 whitespace-nowrap">{t.date}</td>
                            <td className="px-4 py-2">
                              <span className={`px-2 py-1 rounded text-xs ${
                                t.supplier_detected === 'LEROY MERLIN' ? 'bg-green-500/20 text-green-400' :
                                t.supplier_detected === 'BRICO DEPOT' ? 'bg-orange-500/20 text-orange-400' :
                                'bg-slate-700 text-slate-300'
                              }`}>{t.supplier_detected || 'Autre'}</span>
                            </td>
                            <td className="px-4 py-2 text-slate-400 max-w-xs truncate">{t.description || t.full_line?.slice(0, 50)}</td>
                            <td className="px-4 py-2 text-right font-medium text-red-400">{t.amount.toFixed(2)} €</td>
                            <td className="px-4 py-2 text-center">
                              <button 
                                onClick={() => openUploadModal(t)}
                                className="px-3 py-1 bg-blue-500/20 text-blue-400 hover:bg-blue-500/30 rounded text-xs flex items-center gap-1 mx-auto"
                              >
                                <Paperclip className="w-3 h-3" />
                                Ajouter facture
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4">
                  <h4 className="font-medium text-blue-400 mb-2 flex items-center gap-2">
                    <Send className="w-4 h-4" />
                    Pour ajouter ces factures dans Pennylane
                  </h4>
                  <ol className="text-sm text-slate-400 space-y-1 list-decimal list-inside">
                    <li>Exportez le CSV ci-dessus</li>
                    <li>Récupérez les factures correspondantes (Leroy Merlin Pro, etc.)</li>
                    <li>Importez-les dans Pennylane via leur interface</li>
                    <li>Relancez l'analyse pour vérifier</li>
                  </ol>
                </div>
              </>
            )}
          </div>
        )}

        {/* ONGLET: Factures Pennylane sans client */}
        {activeTab === 'pennylane-invoices' && (
          <div className="space-y-6">
            {!pennylaneConnected ? (
              <div className="text-center py-12 bg-slate-800/50 rounded-xl">
                <Settings className="w-16 h-16 text-slate-600 mx-auto mb-4" />
                <h3 className="text-xl font-semibold mb-2">Connectez Pennylane</h3>
                <p className="text-slate-400 mb-6">Pour voir les factures sans dossier client</p>
                <button onClick={() => setShowTokenInput(true)} className="px-6 py-3 bg-blue-600 hover:bg-blue-700 rounded-lg">Connecter</button>
              </div>
            ) : loadingPennylane ? (
              <div className="text-center py-12 bg-slate-800/50 rounded-xl">
                <RefreshCw className="w-16 h-16 text-orange-400 mx-auto mb-4 animate-spin" />
                <h3 className="text-xl font-semibold mb-2">Chargement des factures...</h3>
                <p className="text-slate-400">Récupération depuis Pennylane (peut prendre 1-2 minutes)</p>
              </div>
            ) : pennylaneInvoices.length === 0 ? (
              <div className="text-center py-12 bg-slate-800/50 rounded-xl">
                <CheckCircle2 className="w-16 h-16 text-green-500 mx-auto mb-4" />
                <h3 className="text-xl font-semibold">Toutes les factures sont associées !</h3>
                <p className="text-slate-400 mt-2">Aucune facture Pennylane sans dossier client</p>
                <button onClick={loadPennylaneInvoices} className="mt-4 px-4 py-2 bg-slate-700 hover:bg-slate-600 rounded-lg flex items-center gap-2 mx-auto">
                  <RefreshCw className="w-4 h-4" /> Recharger
                </button>
              </div>
            ) : (
              <>
                <div className="bg-gradient-to-r from-orange-500/20 to-amber-500/20 rounded-xl p-6 border border-orange-500/30">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-lg font-semibold text-orange-400 flex items-center gap-2">
                        <FileQuestion className="w-5 h-5" />
                        {pennylaneInvoices.length} factures Pennylane sans dossier client
                      </h3>
                      <p className="text-slate-400 mt-1">
                        Total: {pennylaneInvoices.reduce((s, inv) => s + inv.amount, 0).toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €
                      </p>
                    </div>
                    <button onClick={loadPennylaneInvoices} className="flex items-center gap-2 px-4 py-2 bg-slate-700 hover:bg-slate-600 rounded-lg">
                      <RefreshCw className="w-4 h-4" /> Recharger
                    </button>
                  </div>
                </div>

                <div className="bg-purple-500/10 border border-purple-500/30 rounded-xl p-4">
                  <div className="flex items-center gap-2">
                    <Sparkles className="w-5 h-5 text-purple-400" />
                    <h4 className="font-medium text-purple-300">Suggestion IA</h4>
                  </div>
                  <p className="text-sm text-slate-400 mt-2">
                    Cliquez sur "Associer" pour qu'une IA analyse la facture et suggère le client CRM correspondant.
                  </p>
                </div>

                <div className="bg-slate-800/50 rounded-xl border border-slate-700/50 overflow-hidden">
                  <div className="overflow-x-auto max-h-[500px]">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-900/50 sticky top-0">
                        <tr>
                          <th className="text-left px-4 py-3 text-slate-400">Date</th>
                          <th className="text-left px-4 py-3 text-slate-400">Fournisseur</th>
                          <th className="text-left px-4 py-3 text-slate-400">Libellé</th>
                          <th className="text-right px-4 py-3 text-slate-400">Montant</th>
                          <th className="text-center px-4 py-3 text-slate-400">Catégories</th>
                          <th className="text-center px-4 py-3 text-slate-400">Action</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-700/50">
                        {pennylaneInvoices.map((inv, i) => (
                          <tr key={inv.id || i} className="hover:bg-slate-700/30">
                            <td className="px-4 py-2 whitespace-nowrap">{inv.date}</td>
                            <td className="px-4 py-2">
                              <span className="px-2 py-1 bg-slate-700 rounded text-xs">{inv.supplier_name}</span>
                            </td>
                            <td className="px-4 py-2 text-slate-400 max-w-xs truncate">{inv.label?.slice(0, 40) || '-'}</td>
                            <td className="px-4 py-2 text-right font-medium text-orange-400">{inv.amount.toFixed(2)} €</td>
                            <td className="px-4 py-2 text-center">
                              <div className="flex flex-wrap gap-1 justify-center">
                                {(inv.categories || []).slice(0, 2).map((cat, ci) => (
                                  <span key={ci} className="px-1 py-0.5 bg-slate-600/50 rounded text-xs text-slate-300">{cat.slice(0, 15)}</span>
                                ))}
                                {(inv.categories || []).length === 0 && <span className="text-slate-500 text-xs">-</span>}
                              </div>
                            </td>
                            <td className="px-4 py-2 text-center">
                              <div className="flex gap-1 justify-center">
                                <button 
                                  onClick={() => openLinkInvoiceModal(inv)}
                                  className="px-3 py-1 bg-orange-500/20 text-orange-400 hover:bg-orange-500/30 rounded text-xs flex items-center gap-1"
                                >
                                  <FolderOpen className="w-3 h-3" />
                                  Associer
                                </button>
                                {inv.public_file_url && (
                                  <a 
                                    href={inv.public_file_url} 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    className="px-2 py-1 bg-blue-500/20 text-blue-400 hover:bg-blue-500/30 rounded text-xs"
                                  >
                                    <ExternalLink className="w-3 h-3" />
                                  </a>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4">
                  <h4 className="font-medium text-amber-400 mb-2">⚠️ Token Pennylane limité</h4>
                  <p className="text-sm text-slate-400">
                    Pour créer automatiquement les dossiers clients dans Pennylane, un token avec permissions d'écriture est nécessaire.
                    Actuellement, seule la consultation est possible. La liaison est sauvegardée localement pour référence.
                  </p>
                </div>
              </>
            )}
          </div>
        )}

        {/* ONGLET: Factures Chantiers */}
        {activeTab === 'factures' && (
          <div className="space-y-6">
            <div className="bg-gradient-to-r from-orange-500/20 to-amber-500/20 rounded-xl p-6 border border-orange-500/30">
              <h3 className="text-lg font-semibold text-orange-400 mb-2 flex items-center gap-2">
                <Link2 className="w-5 h-5" />
                Lier les transactions aux chantiers
              </h3>
              <p className="text-slate-400">Associez chaque transaction CB à un client/chantier.</p>
            </div>

            {unlinkedTransactions.length > 0 ? (
              <div className="bg-slate-800/50 rounded-xl border border-slate-700/50 overflow-hidden">
                <div className="p-4 border-b border-slate-700/50">
                  <h3 className="font-semibold">{unlinkedTransactions.length} transactions à lier</h3>
                </div>
                <div className="overflow-x-auto max-h-[500px]">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-900/50 sticky top-0">
                      <tr>
                        <th className="text-left px-4 py-3 text-slate-400">Date</th>
                        <th className="text-left px-4 py-3 text-slate-400">Fournisseur</th>
                        <th className="text-right px-4 py-3 text-slate-400">Montant</th>
                        <th className="text-center px-4 py-3 text-slate-400">Action</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-700/50">
                      {unlinkedTransactions.filter(t => t.amount <= 50000).slice(0, 100).map((t, i) => (
                        <tr key={i} className="hover:bg-slate-700/30">
                          <td className="px-4 py-3">{t.date}</td>
                          <td className="px-4 py-3">{t.supplier_detected || 'Autre'}</td>
                          <td className="px-4 py-3 text-right font-medium">{t.amount.toFixed(2)} €</td>
                          <td className="px-4 py-3 text-center">
                            <button onClick={() => { setSelectedTransaction(t); setShowLinkModal(true); }} className="px-3 py-1 bg-orange-500/20 text-orange-400 hover:bg-orange-500/30 rounded-lg text-xs flex items-center gap-1 mx-auto">
                              <Link2 className="w-3 h-3" />Lier
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : (
              <div className="text-center py-12 bg-slate-800/50 rounded-xl">
                <CheckCircle2 className="w-16 h-16 text-green-500 mx-auto mb-4" />
                <h3 className="text-xl font-semibold">Toutes les transactions sont liées !</h3>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function TabButton({ active, onClick, icon: Icon, label, badge, color = 'blue' }) {
  const colors = {
    blue: 'border-blue-500 text-blue-400',
    red: 'border-red-500 text-red-400',
    orange: 'border-orange-500 text-orange-400'
  };
  return (
    <button onClick={onClick} className={`flex items-center gap-2 px-4 py-3 border-b-2 whitespace-nowrap ${active ? colors[color] : 'border-transparent text-slate-400 hover:text-white'}`}>
      <Icon className="w-4 h-4" />{label}
      {badge > 0 && <span className={`ml-1 px-2 py-0.5 rounded-full text-xs ${active ? `bg-${color}-500/20` : 'bg-slate-700'}`}>{badge}</span>}
    </button>
  );
}

function StatCard({ icon: Icon, color, title, value }) {
  const colors = { blue: 'bg-blue-500/20 text-blue-400', purple: 'bg-purple-500/20 text-purple-400', green: 'bg-green-500/20 text-green-400', red: 'bg-red-500/20 text-red-400' };
  return (
    <div className="bg-slate-800/50 rounded-xl p-4 border border-slate-700/50">
      <div className="flex items-center gap-3">
        <div className={`w-10 h-10 ${colors[color]} rounded-lg flex items-center justify-center`}><Icon className="w-5 h-5" /></div>
        <div><p className="text-sm text-slate-400">{title}</p><p className="text-2xl font-bold">{value}</p></div>
      </div>
    </div>
  );
}

export default RapprochementPage;
